/* eslint-disable import/max-dependencies */
import { useHandleRequest, useTable } from "@fas/cpa-state-manager/redux/hooks";
import { useSelector } from "react-redux";
import type { StoreWithLoading } from "@fas/cpa-state-manager/services/selectors/loading";
import { getLoadingState } from "@fas/cpa-state-manager/services/selectors/loading";
import type {
  Filters,
  Sorting,
} from "@fas/cpa-state-manager/redux/actions/table/actions";
import {
  getTableExportParams,
  getTableFields,
  getTableSorting,
} from "@fas/cpa-state-manager/services/selectors/table";
import { useEnvironment } from "@fas/cpa-state-manager/hooks";
import { fetchData } from "../useTableFilters/useTableFilters";
import { downloadCsv, getFileNameFromHeaders } from "../../utils";
import {tableConfigMap} from "./tableConfigMap";

export type UseExportReportHook = {
  disabled?: boolean;
  isLoading?: boolean;
  onClick: () => unknown;
};


export const useExportReport = (
  param: keyof typeof tableConfigMap
): UseExportReportHook => {
  const {
    tableKey = "",
    columnsMap = {},
    exportMethod: method,
  } = tableConfigMap[param] || {};
  const environment = useEnvironment();
  const { data } = useTable(tableKey);
  const isLoading: boolean = useSelector((state: StoreWithLoading): boolean =>
    getLoadingState(state, tableKey)
  );
  const isLoadingExport: boolean = useSelector(
    (state: StoreWithLoading): boolean =>
      getLoadingState(state, `${tableKey}-export`)
  );
  const { filters = {} } = useSelector(
    (state) => getTableExportParams(state, tableKey) as { filters?: Filters }
  );
  const sorting: Sorting = useSelector(
    (state): Sorting => getTableSorting(state, tableKey)
  );
  const selectedFields: string[] = useSelector((state): string[] =>
    getTableFields(state, tableKey)
  );
  const {
    changeLoading: changeLoadingExport,
    onFail: onFailExport,
    onFinally: onFinallyExport,
  } = useHandleRequest(`${tableKey}-export`);
  const fields: string[] = Object.keys(columnsMap).filter(
    (k: string): boolean => selectedFields.includes(k)
  );

  const onClick = () => {
    changeLoadingExport(true);
    fetchData<string>({
      filters,
      sorting,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore todo: fix this type generic
      fields: fields.map((key: string) => ({ fieldName: columnsMap[key].field, name: columnsMap[key].label })),
      path: environment.endpoints.post[method]!,
    })
      .then(({ data: csv, headers }) => {
        const name: string = getFileNameFromHeaders(headers);
        downloadCsv(csv, name);
      })
      .catch(onFailExport)
      .finally(onFinallyExport);
  };

  return {
    disabled:
      isLoadingExport || isLoading || data.length === 0 || fields.length === 0,
    isLoading: isLoadingExport,
    onClick,
  };
};
